



























import { Component, Inject, Mixins, Prop } from "vue-property-decorator";
import Context from "@/mixins/context";
import {
  IAvailableTerm,
  IAvailableTermMapped,
  IProductConfigProviderData,
  IProductConfigProviderMethods
} from "@/models/providers/products";
import { BrandConfigKeys } from "@/data/constants";
import { PromotionDisplayTypes } from "@/data/enums/promotions";
import _ from "@/boot/lodash";
import HasValidation from "@/mixins/HasValidation";

enum BillingTermDisplayType {
  CARDS_SELECT = "cards_select",
  RADIO_SELECT = "radio_select"
}

@Component({
  components: {
    "product-terms-card-grid": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productTermsCardGrid.vue"
      ),
    "product-terms-selector": () =>
      import(
        "@/components/app/global/baskets/basket/productView/productTermsSelector.vue"
      )
  }
})
export default class ProductTerms extends Mixins(Context, HasValidation) {
  // Inject

  @Inject({ from: "productConfigProviderData" })
  public readonly $productConfigData!: () => IProductConfigProviderData;

  @Inject({ from: "productConfigProviderMethods" })
  public readonly $productConfigMethods!: IProductConfigProviderMethods;

  // Props

  @Prop({ default: false })
  public disabled!: boolean;

  // Computed

  public get $product() {
    return this.$productConfigData();
  }

  public get mappedTerms(): IAvailableTermMapped[] {
    return _.map(
      this.$product.availableTerms || [],
      (term: IAvailableTerm & { hasMixedPromotions: boolean }) => ({
        hasCustomPrice: this.hasCustomPrice(term),
        hasDiscountedPrice: this.hasDiscountedPrice(term),
        hasMixedPromotions: term.hasMixedPromotions,
        isCustomisable: this.isCustomisable(term),
        isSelected: this.isSelected(term),
        key: this.termKey(term),
        mappedPromotions: this.mappedPromotions(term),
        monthlyComparison: this.monthlyComparison(term),
        months: term.billing_cycle_months,
        name: this.termName(term),
        nowPrice: this.nowPrice(term),
        nowPriceFormatted: this.nowPriceFormatted(term),
        originalPrice: this.originalPrice(term),
        originalPriceFormatted: this.originalPriceFormatted(term),
        wasPrice: this.wasPrice(term),
        wasPriceFormatted: this.wasPriceFormatted(term)
      })
    );
  }

  public get displayType(): BillingTermDisplayType {
    return this.$store.getters["brand/config"][
      BrandConfigKeys.BILLING_TERM_DISPLAY
    ];
  }

  public get component() {
    switch (this.displayType) {
      case BillingTermDisplayType.CARDS_SELECT:
        return "product-terms-card-grid";
      case BillingTermDisplayType.RADIO_SELECT:
      default:
        return "product-terms-selector";
    }
  }

  public get showBillingControl() {
    return (
      // IF: Admin user
      this.isAdmin ||
      // OR: Multiple terms available
      this.mappedTerms.length > 1 ||
      // OR: Only term is NOT one-time
      !!this.mappedTerms[0].months
    );
  }

  // Methods

  public termName(term) {
    return this.$store.getters["constants/getBillingCycleName"](
      term.billing_cycle_months
    );
  }

  public termKey(term) {
    return `${this.$product.productId}_${term.billing_cycle_months}`;
  }

  public isSelected(term) {
    return this.$product.selectedBillingTerm === term.billing_cycle_months;
  }

  public isCustomisable(term) {
    if (this.$product.hasPriceOverrideOption) return false;
    if (!this.isSelected(term)) return false;
    return this.isAdmin;
  }

  public hasCustomPrice(term) {
    return _.isNumber(term.custom_price);
  }

  public hasDiscountedPrice(term) {
    return _.isNumber(term.price_discounted);
  }

  public mappedPromotions(term) {
    if (!term.saving && !term.hasMixedPromotions) return [];
    const displayType =
      this.$store.getters["brand/config"][BrandConfigKeys.SHOW_PROMOTION_AS] ||
      PromotionDisplayTypes.PERCENTAGE;
    const tooltip = term.hasMixedPromotions
      ? this.$t("_sentence.promotion.discount_will_be_auto_applied")
      : term.saving_summary;
    switch (displayType) {
      case PromotionDisplayTypes.NAME:
        return this.$_.map(term.promotionsByName, promo => ({
          label: promo,
          tooltip
        }));
      case PromotionDisplayTypes.PERCENTAGE:
        return [
          {
            label: term.hasMixedPromotions
              ? this.$t("_.on_promotion")
              : this.$t("_.save_percentage", {
                  percentage: term.saving_formatted
                }),
            tooltip
          }
        ];
      case PromotionDisplayTypes.LABEL:
      default:
        return [
          {
            label: this.$t("_.on_promotion"),
            tooltip
          }
        ];
    }
  }

  public monthlyComparison(term) {
    return this.$store.getters["utils/money/trimTrailingZeros"](
      term.monthly_price_from_discounted_formatted ||
        term.monthly_price_from_formatted
    );
  }

  public nowPrice(term) {
    return term.custom_price ?? term.price_discounted ?? term.price;
  }

  public nowPriceFormatted(term) {
    if (!this.nowPrice(term)) return this.$t("_.free");
    return this.$store.getters["utils/money/trimTrailingZeros"](
      term.custom_price_formatted ??
        term.price_discounted_formatted ??
        term.price_formatted
    );
  }

  public wasPrice(term) {
    const hasCustomPrice = this.hasCustomPrice(term);
    const hasDiscountedPrice = this.hasDiscountedPrice(term);
    if (hasCustomPrice || hasDiscountedPrice) return term.price;
    return null;
  }

  public wasPriceFormatted(term) {
    const hasCustomPrice = this.hasCustomPrice(term);
    const hasDiscountedPrice = this.hasDiscountedPrice(term);
    if (hasCustomPrice || hasDiscountedPrice)
      return this.$store.getters["utils/money/trimTrailingZeros"](
        term.price_formatted
      );
    return "";
  }

  public originalPrice(term) {
    return term.price_discounted ?? term.price;
  }

  public originalPriceFormatted(term) {
    return this.$store.getters["utils/money/trimTrailingZeros"](
      term.price_discounted_formatted || term.price_formatted
    );
  }
}
